import { Component, Input, OnInit } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { AppConfig } from 'src/app/configs/app.config';
import { AppRoute } from 'src/app/constants/app.route';
import { DestroyNotifierComponent } from 'src/app/core/component/destroy/destroy-notifier.component';
import { ChartService } from 'src/app/core/services/chart/chart.service';
import { ChartNg2Data } from 'src/app/core/services/chart/interfaces/chart-data-ng2';
import { DashboardService } from 'src/app/core/services/http/dashboard/dashboard.service';
import { StudentMetrics } from 'src/app/core/services/http/dashboard/interfaces/student-metrics';
import { User } from '../../../core/services/http/user/interfaces/user';
import { StudentRestrictionMetrics } from '../../../core/services/http/dashboard/interfaces/student-restriction-metrics';
import { Color } from 'ng2-charts';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { MyProfileEditModalComponent } from '../my-profile-edit-modal/my-profile-edit-modal.component';
import { ConfirmationModal } from '../../../shared/confirmation-modal/interfaces/confirmation.modal';
import { AuthenticatedUserService } from 'src/app/core/services/http/authenticated-user/authenticated-user.service';

@Component({
  selector: 'app-student-dashboard',
  templateUrl: './student-dashboard.component.html',
  styleUrls: ['./student-dashboard.component.scss']
})
export class StudentDashboardComponent extends DestroyNotifierComponent implements OnInit {
  @Input() user: User | undefined;

  appRoute = AppRoute;
  appConfig = AppConfig;
  route = AppRoute.MyProfile;

  studentMetrics: StudentMetrics | undefined;
  studentRestriction: StudentRestrictionMetrics | undefined;
  chartData: Record<string, ChartNg2Data> = {};

  constructor(
    private dashboardService: DashboardService,
    private chartService: ChartService,
    private translate: TranslateService,
    private modalService: BsModalService,
    private authenticatedUserService: AuthenticatedUserService
    ) {
    super();
  }

  ngOnInit(): void {
    // remove pie chart borders
    this.appConfig.ChartOptions = {
      ... this.appConfig.ChartOptions, elements: {
        arc: {
          borderWidth: 0
        }
      }
    };

    this.updateData();
  }

  async onEditUser(): Promise<void> {
    const translations = await this.translate.get(['UPDATE_USER', 'UPDATE']).toPromise();

    const initialState = {
      modal: {
        title: translations.UPDATE_USER,
        buttonText: translations.UPDATE,
        callback: () => {
          this.onConfirmEditUser();
        }
      } as ConfirmationModal
    };

    this.modalService.show(MyProfileEditModalComponent, { initialState });
  }

  public applyLabelClass(index: number, colors: Color[]): string {
    const backgroundColor: any = colors[0]?.backgroundColor;

    if (backgroundColor === null && backgroundColor.hasOwnProperty('length')) {
      return '';
    }

    const hexColor: string = backgroundColor[index];

    if (hexColor === undefined) {
      return '';
    }

    return this.chartService.applyLabelClass(hexColor);
  }

  private onConfirmEditUser(): void {
    this.loadUser();
  }

  private loadUser(): void {
    this.authenticatedUserService
      .getAuthenticatedUser()
      .pipe(takeUntil(this.destroyNotify))
      .subscribe(user => {
        this.user = user;
      });
  }

  private async updateData(): Promise<void> {
    this.studentMetrics = await this.getStudentMetrics();
    const studentMetrics: Record<string, Record<string, number>> = {
      activitiesCurrentSchoolYear: this.studentMetrics.activities
    };

    this.chartData = await this.chartService.metricsToNg2Chart(studentMetrics);
    this.studentRestriction = await this.getStudentsRestrictions();
  }

  private async getStudentMetrics(): Promise<StudentMetrics> {
    return await this.dashboardService.getStudentsMetrics().pipe(takeUntil(this.destroyNotify)).toPromise();
  }

  private async getStudentsRestrictions(): Promise<StudentRestrictionMetrics> {
    return await this.dashboardService.getStudentsRestrictions().pipe(takeUntil(this.destroyNotify)).toPromise();
  }
}
